import { fetchConfig } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { morphSection } from '@theme/section-renderer';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * "Switch to subscription" on the cart drawer's one-time Pouch line (snippets/c-cart-plan.liquid).
 * Adds the Welcome Kit on the selling plan for the dog's size, carrying the size properties the PDP
 * set, removes the Pouch line by its key, morphs the drawer from the response and announces the
 * change from cart-items-component, as Horizon's own updateQuantity does.
 *
 * - A click is ignored while a quantity change is queued (Horizon debounces them 300ms) or running:
 *   that change addresses a line number the add would shift onto the kit. It re-renders the drawer,
 *   and a Pouch at quantity 2 has no switch card.
 * - Also ignored while a discount code is being applied or removed: its response would render over
 *   the drawer mid-switch, or after it.
 * - The whole cart is inert while it runs, checkout included, and stays inert through any render that
 *   lands meanwhile (morph drops attributes the server HTML lacks, so an observer puts it back).
 * - It reads the cart first and acts only if the cart is still what the drawer shows: this Pouch at
 *   quantity 1, plus at most this exact kit (same plan and size) from an earlier attempt, in which
 *   case the add is skipped. Anything else means the drawer is stale, so the page reloads.
 * - Once the add has been sent, any failure reloads the page so it shows the cart as it is, unless
 *   Shopify refused the add (4xx: nothing changed), which shows the card's error instead.
 *
 * Loaded by snippets/cart-drawer.liquid (a Roots edit): a script inside a re-rendered drawer does not
 * execute. The customElements guard makes a double load harmless.
 */
const QUANTITY_DEBOUNCE_MS = 300; // component-cart-items.js #debouncedOnChange
let lastQuantityChange = 0;
document.addEventListener(ThemeEvents.quantitySelectorUpdate, () => {
  lastQuantityChange = performance.now();
});

class CartSwitch extends HTMLElement {
  connectedCallback() {
    this.querySelector('.c-cart-plan__switch')?.addEventListener('click', this.#switch);
  }

  #switch = async (/** @type {Event} */ event) => {
    const button = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const cartItems = /** @type {HTMLElement | null} */ (this.closest('cart-items-component'));
    const sectionId = cartItems?.dataset.sectionId;
    if (!cartItems || !sectionId) return;
    if (button.getAttribute('aria-busy') === 'true') return;
    if (cartItems.classList.contains('cart-items-disabled')) return;
    if (performance.now() - lastQuantityChange < QUANTITY_DEBOUNCE_MS + 100) return;
    // A discount in flight would render its own response over the drawer mid-switch, or after it.
    if (cartItems.querySelector('cart-discount-component[aria-busy="true"]')) return;

    button.setAttribute('aria-busy', 'true');
    const error = /** @type {HTMLElement | null} */ (this.querySelector('.c-cart-plan__error'));
    if (error) error.hidden = true;
    cartItems.setAttribute('inert', '');
    // Any render (morph drops attributes the server HTML lacks) would unlock the cart mid-switch.
    const lock = new MutationObserver(() => {
      if (!cartItems.hasAttribute('inert')) cartItems.setAttribute('inert', '');
    });
    lock.observe(cartItems, { attributeFilter: ['inert'] });
    const unlock = () => {
      lock.disconnect();
      cartItems.removeAttribute('inert');
    };

    const { key, variant, plan, size, sizeLabel, sizeConfirmed } = this.dataset;
    /** @param {any} item */
    const isThisKit = (item) =>
      item.variant_id === Number(variant) &&
      item.selling_plan_allocation?.selling_plan?.id === Number(plan) &&
      item.properties?._dog_size === size &&
      item.quantity === 1;
    let mutated = false;
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`);
      if (!response.ok) throw new Error(`cart read failed: ${response.status}`);
      const { items } = await response.json();
      const pouch = items.find((/** @type {any} */ item) => item.key === key);
      const others = items.filter((/** @type {any} */ item) => item !== pouch);
      if (pouch?.quantity !== 1 || others.length > 1 || (others.length === 1 && !isThisKit(others[0]))) {
        window.location.reload();
        return;
      }

      mutated = true;
      if (others.length === 0) {
        const added = await fetch(
          Theme.routes.cart_add_url,
          fetchConfig('json', {
            body: JSON.stringify({
              items: [
                {
                  id: Number(variant),
                  quantity: 1,
                  selling_plan: Number(plan),
                  properties: { 'Dog size': sizeLabel, _dog_size: size, _dog_size_confirmed: sizeConfirmed },
                },
              ],
            }),
          })
        );
        if (!added.ok) {
          if (added.status < 500) mutated = false;
          throw new Error(`add failed: ${added.status}`);
        }
      }

      // By key, not line number: the add can reorder lines.
      const removed = await fetch(
        Theme.routes.cart_change_url,
        fetchConfig('json', {
          body: JSON.stringify({ id: key, quantity: 0, sections: sectionId, sections_url: window.location.pathname }),
        })
      );
      const result = await removed.json();
      if (!removed.ok || result.errors) throw new Error(`remove failed: ${result.errors ?? removed.status}`);
      const html = result.sections?.[sectionId];
      if (!html) throw new Error('no section html');

      morphSection(sectionId, html, { mode: 'hydration' });
      unlock();
      for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
        /** @type {any} */ (selector).updateButtonStates?.();
      }
      // From cart-items-component, so it skips its own re-render; the drawer, icon and others update.
      (cartItems.isConnected ? cartItems : document).dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'update',
          context: 'cart',
          lines: [{ id: key ?? '', quantity: 0 }],
          promise: Promise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(result),
            detail: {
              sections: result.sections,
              items: result.items,
              itemCount: result.item_count,
              source: 'c-cart-switch',
              didError: false,
            },
          }),
        })
      );
    } catch (failure) {
      console.error('[c-cart-plan]', failure);
      if (mutated) {
        window.location.reload();
        return;
      }
      unlock();
      if (error) error.hidden = false;
      button.removeAttribute('aria-busy');
      button.focus();
    }
  };
}

if (!customElements.get('c-cart-switch')) customElements.define('c-cart-switch', CartSwitch);
